<template>
  <div class="page-container">
    <div class="page-title-container">
      <h1 class="page-title">Descansos médicos</h1>
    </div>
    <div class="table-container">
      <div class="table-header">
        <div class="d-flex flex-column flex-md-row justify-content-between align-items-start position-relative">
          <div class="w-full w-md-auto md-flex-1">
            <div class="d-flex justify-content-between align-items-center justify-content-lg-start">
              <button
                type="button"
                class="filter d-flex align-items-center justify-content-between border-0"
                @click="showFilters = !showFilters"
              >
                <i class="ri-filter-3-line text-dark font-weight-bold"></i>
                <div>Filtrar</div>
              </button>

              <div class="d-md-none">
                <button
                  type="button"
                  class="button-tooltip text-dark font-weight-bold m-0"
                  v-b-tooltip.hover
                  title="Recargar"
                  @click="loadData()"
                >
                  <i class="ri-loop-right-line"></i>
                </button>
                <button
                  type="button"
                  class="button-tooltip text-dark font-weight-bold mr-0"
                  v-b-tooltip.hover
                  title="Descargar Excel"
                  @click="downloadReport()"
                >
                  <i class="ri-file-excel-line"></i>
                </button>
              </div>
            </div>
          </div>

          <section class="w-full w-md-auto md-flex-1 d-flex justify-content-end align-items-center mt-2 mt-md-0">
            <div class="d-none d-md-flex">
              <button
                type="button"
                class="button-tooltip text-dark font-weight-bold"
                v-b-tooltip.hover
                title="Recargar"
                @click="loadData()"
              >
                <i class="ri-loop-right-line"></i>
              </button>
              <button
                type="button"
                class="button-tooltip text-dark font-weight-bold ml-0"
                v-b-tooltip.hover
                title="Descargar Excel"
                @click="downloadReport()"
              >
                <i class="ri-file-excel-line"></i>
              </button>
            </div>
            <div class="w-full w-md-auto d-flex flex-column flex-md-row">
              <div class="text-right mt-2 mt-md-0">
                <b-button
                  :to="{ name: 'medical-leave-create' }"
                  variant="none"
                  class="button button-primary ml-md-2"
                >Agregar</b-button>
              </div>
            </div>
          </section>

          <filter-menu
            :show="showFilters"
            @hide="showFilters = false"
            :options="filterOptions"
            :selectedOptionFilter="filters"
            @setFilters="setFilters"
          ></filter-menu>
        </div>
      </div>

      <!-- Tabla desktop -->
      <div class="table-responsive d-none d-md-block">
        <table class="custom-table">
          <thead>
            <tr>
              <th>EMPLEADO</th>
              <th>EMPRESA</th>
              <th>TIPO</th>
              <th>FECHA INICIO</th>
              <th>FECHA FIN</th>
              <th>DÍAS</th>
              <th>ESTADO</th>
              <th>CASO</th>
              <th>TRÁMITE</th>
              <th>MONTO PAGADO</th>
              <th>ENCARGADO</th>
              <th class="text-center">OPCIONES</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="list.length === 0">
              <td colspan="12" class="text-center text-muted py-4">No se encontraron descansos médicos</td>
            </tr>
            <tr v-for="item in list" :key="item.id">
              <td>{{ item.employeeName }}</td>
              <td>{{ item.companyName }}</td>
              <td>
                <span :class="['badge', getTypeColor(item.leaveType)]">{{ getTypeLabel(item.leaveType) }}</span>
              </td>
              <td>{{ item.startDate }}</td>
              <td>{{ item.endDate }}</td>
              <td>{{ item.calendarDays }}</td>
              <td>
                <span :class="['badge', getStatusColor(item.status)]">{{ item.status }}</span>
              </td>
              <td>
                <span :class="['badge', getCaseStatusColor(item.caseStatus)]">
                  {{ getCaseStatusLabel(item.caseStatus) }}
                </span>
              </td>
              <td>
                <template v-if="item.caseStatus === 'SUBSIDIO' && item.processStatus">
                  <span :class="['badge', getProcessStatusColor(item.processStatus)]">
                    {{ getProcessStatusLabel(item.processStatus) }}
                  </span>
                </template>
                <template v-else>-</template>
              </td>
              <td>{{ formatCurrency(item.amountPaid) }}</td>
              <td>{{ item.personInCharge || '-' }}</td>
              <td class="text-center">
                <b-dropdown variant="link" no-caret toggle-class="p-0">
                  <template #button-content>
                    <i class="ri-more-2-fill text-dark"></i>
                  </template>
                  <b-dropdown-item :to="{ name: 'medical-leave-detail', params: { id: item.id } }">
                    Ver detalle
                  </b-dropdown-item>
                  <b-dropdown-item v-if="item.status === 'ACTIVO'" @click="openFinalize(item)">
                    Finalizar
                  </b-dropdown-item>
                  <b-dropdown-item v-if="item.status === 'ACTIVO'" @click="openCancel(item)">
                    Cancelar
                  </b-dropdown-item>
                </b-dropdown>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Tarjetas mobile -->
      <div class="d-md-none">
        <div v-if="list.length === 0" class="text-center text-muted py-4">No se encontraron descansos médicos</div>
        <div v-for="item in list" :key="item.id" class="mobile-card mb-3 p-3 border rounded">
          <div class="d-flex justify-content-between align-items-start">
            <div>
              <strong>{{ item.employeeName }}</strong>
              <div class="text-muted small">{{ item.companyName }}</div>
            </div>
            <span :class="['badge', getStatusColor(item.status)]">{{ item.status }}</span>
          </div>
          <div class="mt-2 small">
            <span :class="['badge', getTypeColor(item.leaveType), 'mr-2']">{{ getTypeLabel(item.leaveType) }}</span>
            <span>{{ item.startDate }} – {{ item.endDate }}</span>
            <span class="ml-2">({{ item.calendarDays }} días)</span>
          </div>
          <div class="mt-2 small d-flex flex-wrap">
            <span :class="['badge mr-2', getCaseStatusColor(item.caseStatus)]">
              {{ getCaseStatusLabel(item.caseStatus) }}
            </span>
            <span v-if="item.caseStatus === 'SUBSIDIO' && item.processStatus"
                  :class="['badge mr-2', getProcessStatusColor(item.processStatus)]">
              {{ getProcessStatusLabel(item.processStatus) }}
            </span>
            <span v-if="item.amountPaid" class="text-primary font-weight-bold">
              {{ formatCurrency(item.amountPaid) }}
            </span>
          </div>
          <div v-if="item.personInCharge" class="mt-1 small text-muted">
            Encargado: {{ item.personInCharge }}
          </div>
          <div class="mt-2 d-flex">
            <router-link :to="{ name: 'medical-leave-detail', params: { id: item.id } }" class="button button-light button-sm mr-2">
              Ver
            </router-link>
            <button v-if="item.status === 'ACTIVO'" type="button" class="button button-primary button-sm mr-2" @click="openFinalize(item)">Finalizar</button>
            <button v-if="item.status === 'ACTIVO'" type="button" class="button button-light button-sm" @click="openCancel(item)">Cancelar</button>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal: Finalizar -->
    <b-modal
      id="modal-finalize"
      title="Finalizar descanso médico"
      @ok="confirmFinalize"
      ok-title="Finalizar"
      cancel-title="Cancelar"
      ok-variant="primary"
      :ok-disabled="!finalizeDate"
    >
      <div class="form-group">
        <label>Fecha real de fin <span class="text-danger">*</span></label>
        <FormDatepicker v-model="finalizeDate" />
      </div>
      <div v-if="selectedItem && finalizeDate" class="text-muted small mt-2">
        Días: {{ computeFinalizeDays() }}
      </div>
    </b-modal>

    <!-- Modal: Cancelar -->
    <b-modal
      id="modal-cancel"
      title="Cancelar descanso médico"
      @ok="confirmCancel"
      ok-title="Cancelar descanso"
      cancel-title="Volver"
      ok-variant="danger"
      :ok-disabled="!cancelReason.trim()"
    >
      <div class="form-group">
        <label>Motivo de cancelación <span class="text-danger">*</span></label>
        <b-form-textarea
          v-model="cancelReason"
          rows="3"
          placeholder="Ingrese el motivo de la cancelación"
        />
      </div>
    </b-modal>
  </div>
</template>

<script>
import {
  Constants,
  Alert,
  FilterMenu,
  FilterMixin,
  FormDatepicker,
} from 'wize-admin'
import { MedicalLeaveService, CompanyService } from '@/core/services'
import { Constants as LocalConstants } from '@/core/utils'

export default {
  name: 'MedicalLeaveList',
  components: { FilterMenu, FormDatepicker },
  mixins: [FilterMixin],
  data() {
    return {
      list: [],
      showFilters: false,
      filterOptions: [
        {
          label: 'Empresa',
          key: 'companyId',
          type: Constants.TypeFilters.SINGLE,
          options: [],
        },
        {
          label: 'Tipo',
          key: 'leaveType',
          type: Constants.TypeFilters.SINGLE,
          options: Object.values(LocalConstants.MedicalLeaveType).map(t => ({ key: t.key, label: t.label })),
        },
        {
          label: 'Estado',
          key: 'status',
          type: Constants.TypeFilters.SINGLE,
          options: Object.values(LocalConstants.MedicalLeaveStatus).map(s => ({ key: s.key, label: s.label })),
        },
        {
          label: 'Estado del caso',
          key: 'caseStatus',
          type: Constants.TypeFilters.SINGLE,
          options: Object.values(LocalConstants.MedicalLeaveCaseStatus).map(s => ({ key: s.key, label: s.label })),
        },
        {
          label: 'Estado del trámite',
          key: 'processStatus',
          type: Constants.TypeFilters.SINGLE,
          options: Object.values(LocalConstants.MedicalLeaveProcessStatus).map(s => ({ key: s.key, label: s.label })),
        },
        {
          label: 'Fecha de inicio',
          key: 'startDate',
          type: Constants.TypeFilters.DATE,
          isRange: true,
        },
      ],
      selectedItem: null,
      finalizeDate: null,
      cancelReason: '',
    }
  },
  methods: {
    buildFilterParams() {
      const params = {}
      if (this.filters.companyId)     params.companyId     = this.filters.companyId
      if (this.filters.leaveType)     params.leaveType     = this.filters.leaveType
      if (this.filters.status)        params.status        = this.filters.status
      if (this.filters.caseStatus)    params.caseStatus    = this.filters.caseStatus
      if (this.filters.processStatus) params.processStatus = this.filters.processStatus
      if (this.filters.startDate)     params.startDate     = this.filters.startDate
      if (this.filters.endDate)       params.endDate       = this.filters.endDate
      return params
    },
    async loadData() {
      try {
        this.$store.dispatch('app/loading', true)
        const resp = await MedicalLeaveService.getAll(this.buildFilterParams())
        this.list = resp.payload || []
      } catch (e) {
        Alert.error(e.errors ? e.errors.message : 'Error al cargar descansos médicos')
      } finally {
        this.$store.dispatch('app/loading', false)
      }
    },
    async downloadReport() {
      try {
        this.$store.dispatch('app/loading', true)
        const resp = await MedicalLeaveService.downloadXlsx(this.buildFilterParams())
        const today = new Date().toISOString().split('T')[0]
        const url = window.URL.createObjectURL(resp.data)
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', `descansos-medicos-${today}.xlsx`)
        document.body.appendChild(link)
        link.click()
        link.remove()
        window.URL.revokeObjectURL(url)
      } catch (e) {
        Alert.error('Error al descargar el reporte')
      } finally {
        this.$store.dispatch('app/loading', false)
      }
    },
    getTypeColor(leaveType) {
      const found = Object.values(LocalConstants.MedicalLeaveType).find(t => t.key === leaveType)
      return found ? found.color : 'badge-gray'
    },
    getTypeLabel(leaveType) {
      const found = Object.values(LocalConstants.MedicalLeaveType).find(t => t.key === leaveType)
      return found ? found.label : leaveType
    },
    getStatusColor(status) {
      const found = Object.values(LocalConstants.MedicalLeaveStatus).find(s => s.key === status)
      return found ? found.color : 'badge-gray'
    },
    getCaseStatusColor(caseStatus) {
      const found = Object.values(LocalConstants.MedicalLeaveCaseStatus).find(s => s.key === caseStatus)
      return found ? found.color : 'badge-gray'
    },
    getCaseStatusLabel(caseStatus) {
      const found = Object.values(LocalConstants.MedicalLeaveCaseStatus).find(s => s.key === caseStatus)
      return found ? found.label : (caseStatus || '-')
    },
    getProcessStatusColor(processStatus) {
      const found = Object.values(LocalConstants.MedicalLeaveProcessStatus).find(s => s.key === processStatus)
      return found ? found.color : 'badge-gray'
    },
    getProcessStatusLabel(processStatus) {
      const found = Object.values(LocalConstants.MedicalLeaveProcessStatus).find(s => s.key === processStatus)
      return found ? found.label : (processStatus || '-')
    },
    formatCurrency(amount) {
      if (amount == null) return '-'
      return `S/ ${Number(amount).toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
    },
    openFinalize(item) {
      this.selectedItem = item
      this.finalizeDate = null
      this.$bvModal.show('modal-finalize')
    },
    openCancel(item) {
      this.selectedItem = item
      this.cancelReason = ''
      this.$bvModal.show('modal-cancel')
    },
    computeFinalizeDays() {
      if (!this.selectedItem || !this.finalizeDate) return '-'
      const start = new Date(this.selectedItem.startDate + 'T00:00:00')
      const end   = new Date(this.finalizeDate          + 'T00:00:00')
      return Math.round((end - start) / (1000 * 60 * 60 * 24)) + 1
    },
    async confirmFinalize() {
      try {
        this.$store.dispatch('app/loading', true)
        await MedicalLeaveService.finalize(this.selectedItem.id, { actualEndDate: this.finalizeDate })
        Alert.success('Descanso médico finalizado correctamente')
        this.loadData()
      } catch (e) {
        Alert.error(e.errors ? e.errors.message : 'Error al finalizar')
      } finally {
        this.$store.dispatch('app/loading', false)
      }
    },
    async confirmCancel() {
      try {
        this.$store.dispatch('app/loading', true)
        await MedicalLeaveService.cancel(this.selectedItem.id, { cancellationReason: this.cancelReason })
        Alert.success('Descanso médico cancelado')
        this.loadData()
      } catch (e) {
        Alert.error(e.errors ? e.errors.message : 'Error al cancelar')
      } finally {
        this.$store.dispatch('app/loading', false)
      }
    },
  },
  async created() {
    try {
      const resp = await CompanyService.list()
      const companyOptions = (resp.payload || []).map(c => ({ key: c.id, label: c.businessName || c.name }))
      this.filterOptions[0].options = companyOptions
    } catch (e) {
      console.error(e)
    }
    this.loadData()
  },
}
</script>
